/**
 * App Root Component
 * Sets up routing, providers, and lazy-loaded pages
 */
import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import { AcademicYearProvider } from './contexts/AcademicYearContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import Loading from './components/common/Loading';
import MaintenanceGuard from './components/common/MaintenanceGuard';
import { ROUTES } from './config/routes';
import { ROLES } from './config/constants';

// ─── Lazy-loaded Pages ───
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PublicResult = lazy(() => import('./pages/PublicResult'));
const ResultVerification = lazy(() => import('./pages/results/ResultVerification'));
const StudentVerification = lazy(() => import('./pages/verify/StudentVerification'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AcademicYearPage = lazy(() => import('./pages/AcademicYearPage'));
const SetupWizard = lazy(() => import('./pages/setup/SetupWizard'));
const SubjectManagement = lazy(() => import('./pages/setup/SubjectManagement'));
const ClassSubjectMapping = lazy(() => import('./pages/setup/ClassSubjectMapping'));
const GradingPolicyManagement = lazy(() => import('./pages/setup/GradingPolicyManagement'));
const TeacherImport = lazy(() => import('./pages/setup/TeacherImport'));

const ExamSessionManagement = lazy(() => import('./pages/exam/ExamSessionManagement'));
const EnrollmentManagement = lazy(() => import('./pages/exam/EnrollmentManagement'));
const TeacherAssignment = lazy(() => import('./pages/setup/TeacherAssignment'));
const MarkEntry = lazy(() => import('./pages/marks/MarkEntry'));
const MarkManagement = lazy(() => import('./pages/marks/MarkManagement'));
const UpdateRequests = lazy(() => import('./pages/marks/UpdateRequests'));
const ResultGeneration = lazy(() => import('./pages/results/ResultGeneration'));
const AdminResultSearch = lazy(() => import('./pages/results/AdminResultSearch'));
const ReportsDashboard = lazy(() => import('./pages/reports/ReportsDashboard'));
const TabulationSheetPage = lazy(() => import('./pages/reports/TabulationSheetPage'));
const ClassMarksheetsPage = lazy(() => import('./pages/reports/ClassMarksheetsPage'));
const AdmitCardPage = lazy(() => import('./pages/reports/AdmitCardPage'));
const AuditLogs = lazy(() => import('./pages/audit/AuditLogs'));
const Settings = lazy(() => import('./pages/Settings'));
const NoticeBoard = lazy(() => import('./pages/notifications/NoticeBoard'));
const Notifications = lazy(() => import('./pages/notifications/Notifications'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));

// ─── React Query Client ───
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <MaintenanceGuard />
        <AuthProvider>
          <AcademicYearProvider>
            <Suspense fallback={<Loading fullScreen message="Loading..." />}>
              <Routes>
                {/* Public Routes */}
                <Route path={ROUTES.LOGIN} element={<LoginPage />} />
                <Route path={ROUTES.PUBLIC_RESULTS} element={<PublicResult />} />
                <Route path={ROUTES.VERIFY_RESULT} element={<ResultVerification />} />
                <Route path={ROUTES.VERIFY_STUDENT} element={<StudentVerification />} />
                <Route path={ROUTES.MAINTENANCE} element={<MaintenancePage />} />

                {/* Protected Routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
                  <Route path={ROUTES.MARK_ENTRY} element={<MarkEntry />} />
                  <Route path={ROUTES.NOTIFICATIONS} element={<Notifications />} />

                  {/* ─── Admin Only Routes ─── */}
                  <Route element={<ProtectedRoute requiredRole={ROLES.ADMIN}><Outlet /></ProtectedRoute>}>
                    <Route path={ROUTES.ACADEMIC_YEAR} element={<AcademicYearPage />} />
                    <Route path={ROUTES.SETUP} element={<SetupWizard />} />

                    {/* Phase 2: Setup & Configuration */}
                    <Route path={ROUTES.SUBJECTS} element={<SubjectManagement />} />
                    <Route path={ROUTES.CLASS_SUBJECTS} element={<ClassSubjectMapping />} />
                    <Route path={ROUTES.GRADING_POLICY} element={<GradingPolicyManagement />} />

                    {/* Phase 3: Integration Layer */}
                    <Route path={ROUTES.TEACHERS} element={<TeacherImport />} />

                    {/* Phase 4: Exam Management */}
                    <Route path={ROUTES.EXAMS} element={<ExamSessionManagement />} />
                    <Route path={ROUTES.ENROLLMENTS} element={<EnrollmentManagement />} />
                    <Route path={ROUTES.TEACHER_ASSIGNMENTS} element={<TeacherAssignment />} />

                    {/* Phase 5: Mark Management */}
                    <Route path={ROUTES.MARK_MANAGEMENT} element={<MarkManagement />} />
                    <Route path={ROUTES.UPDATE_REQUESTS} element={<UpdateRequests />} />

                    {/* Phase 6: Result Engine */}
                    <Route path={ROUTES.RESULTS} element={<ResultGeneration />} />
                    <Route path={ROUTES.RESULT_SEARCH} element={<AdminResultSearch />} />

                    {/* Phase 7: Reports */}
                    <Route path={ROUTES.REPORTS} element={<ReportsDashboard />} />
                    <Route path={ROUTES.TABULATION_SHEET} element={<TabulationSheetPage />} />
                    <Route path={ROUTES.CLASS_MARKSHEETS} element={<ClassMarksheetsPage />} />
                    <Route path={ROUTES.EXAM_ADMIT} element={<AdmitCardPage />} />

                    {/* Phase 8: Admin & Settings */}
                    <Route path={ROUTES.AUDIT_LOGS} element={<AuditLogs />} />
                    <Route path={ROUTES.SETTINGS} element={<Settings />} />

                    {/* Notification Center */}
                    <Route path={ROUTES.NOTICE_BOARD} element={<NoticeBoard />} />
                  </Route>
                </Route>

                {/* Redirects */}
                <Route path="/" element={<PublicResult />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>

            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#1e293b',
                  color: '#e2e8f0',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  fontSize: '14px',
                },
                success: {
                  iconTheme: { primary: '#14b8a6', secondary: '#ffffff' },
                },
                error: {
                  iconTheme: { primary: '#ef4444', secondary: '#ffffff' },
                },
              }}
            />
          </AcademicYearProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

